import { CURRENCIES } from "./countries";

/**
 * Checkout for v2.typora.io/purchase, following store.typora.io: the country
 * picks 2Checkout (Verifone) InLine Checkout or the FastSpring popup. After
 * payment the page polls TyporaStore (`POST /api/purchase/status`) for the
 * order's licenses, with proof of the order it paid for:
 *
 * - 2Checkout: the parameters its return URL carries back to this page
 *   (`refno`, `total`, `total-currency`, `signature`), signed with the
 *   Buy-Link Secret Word.
 *
 * FastSpring needs no store: its `order.completed` webhook has a browser
 * script, so the popup forwards the whole payload, license codes included,
 * to this page (`data-popup-webhook-received`).
 *
 * See docs/purchase.md.
 */

export type Provider = "2checkout" | "fastspring";

/** The Aliyun copy (docs/deploy-aliyun.md): straight to 2Checkout's Alipay checkout in CNY. */
export const CHINA_MIRROR = import.meta.env.VITE_CHINA_MIRROR === "1";

const STORE_ORIGIN = import.meta.env.VITE_STORE_ORIGIN ?? "https://store.typora.io";
const TWO_CHECKOUT_MERCHANT = "251894663968";
const TWO_CHECKOUT_PRODUCT = "typora";
const FASTSPRING_STOREFRONT = "typora.onfastspring.com/popup-typora";
const FASTSPRING_PRODUCT = "typora-editor";

/** OFAC-sanctioned: 2Checkout does not sell there. */
const FASTSPRING_ONLY = new Set(["IR", "CU", "LY", "SS", "SY"]);
/** Where store.typora.io sends buyers to 2Checkout; FastSpring elsewhere. */
const TWO_CHECKOUT_FIRST = new Set(["CN", "RU", "KR", "MY", "TH", "TR", "AR", "US"]);

/**
 * `alternative`: the buyer had trouble and asked for the other provider.
 * `?use2c=true` / `?useFS` force one, as on store.typora.io.
 */
export function providerFor(country: string, alternative = false): Provider {
  const query = window.location.search;
  if (/[?&]useFS\b/i.test(query)) return "fastspring";
  if (/[?&]use2c=true\b/i.test(query) && !FASTSPRING_ONLY.has(country)) return "2checkout";
  if (FASTSPRING_ONLY.has(country)) return "fastspring";
  const preferred: Provider = TWO_CHECKOUT_FIRST.has(country) ? "2checkout" : "fastspring";
  if (!alternative) return preferred;
  return preferred === "2checkout" ? "fastspring" : "2checkout";
}

/** Both checkouts show English for Chinese and Japanese, as the store does. */
function checkoutLanguage(): string {
  const language = (navigator.languages?.[0] ?? navigator.language ?? "en").slice(0, 2).toLowerCase();
  return language === "zh" || language === "ja" ? "en" : language;
}

/** What the store needs to trust that this page paid for a 2Checkout order. */
export type OrderProof = {
  provider: "2checkout";
  refno: string;
  total: string;
  "total-currency": string;
  signature: string;
};

const RETURN_PARAMS = ["refno", "total", "total-currency", "signature"] as const;

/** The proof on a 2Checkout return URL (`/purchase?refno=…&signature=…`), if this is one. */
export function returnedOrderProof(search: string): OrderProof | null {
  const query = new URLSearchParams(search);
  const [refno, total, currency, signature] = RETURN_PARAMS.map((name) => query.get(name) ?? "");
  if (!refno || !total || !currency || !signature) return null;
  return { provider: "2checkout", refno, total, "total-currency": currency, signature };
}

/** The URL without the return parameters, for `history.replaceState`. */
export function withoutReturnParams(url: string): string {
  const parsed = new URL(url);
  for (const name of RETURN_PARAMS) parsed.searchParams.delete(name);
  return parsed.pathname + parsed.search + parsed.hash;
}

function loadScript(src: string, attributes: Record<string, string> = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    for (const [name, value] of Object.entries(attributes)) script.setAttribute(name, value);
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Could not load ${src}`));
    document.head.append(script);
  });
}

/** 2Checkout's hosted checkout, in CNY with Alipay, for buyers in China. */
export function alipayCheckoutUrl(quantity: number): string {
  const checkout =
    `https://secure.2checkout.com/order/checkout.php?PRODS=37673796&QTY=${quantity}&CART=1&&SHORT_FORM=1` +
    "&ORDERSTYLE=nLWw4pa5hHU=&CARD=2&CURRENCY=CNY&LANGUAGES=en&PAY_TYPE=ALIPAY";
  return (
    `https://secure.2checkout.com/order/pf.php?MERCHANT=${TWO_CHECKOUT_MERCHANT}&BILL_COUNTRYCODE=CN` +
    `&URL=${encodeURIComponent(checkout)}`
  );
}

// MARK: - 2Checkout InLine Checkout

type TwoCoInlineCart = {
  setup: { setConfig(namespace: string, config: object): void };
  register(): void;
  billing: { setCountry(code: string): void };
  cart: {
    setCurrency(code: string): void;
    setLanguage(code: string): void;
    setReturnMethod(method: { type: "redirect"; url: string }): void;
    checkout(): void;
  };
  products: { removeAll(): void; add(product: { code: string; quantity: number }): void };
  events: { subscribe(event: string, callback: (data?: unknown) => void): void };
};

declare global {
  interface Window {
    TwoCoInlineCart?: TwoCoInlineCart;
    fastspring?: {
      builder: {
        country(code: string): void;
        language(code: string): void;
        update(product: string, quantity: number): void;
        checkout(): void;
      };
    };
    typoraPurchasePopupClosed?: (order: { id?: string; reference?: string } | null) => void;
    typoraPurchaseWebhookReceived?: (data: unknown) => void;
  }
}

let twoCheckout: Promise<TwoCoInlineCart> | null = null;
/** The open cart's `cart:closed`; cart events can't be unsubscribed. */
let onTwoCheckoutClosed: (() => void) | null = null;

function loadTwoCheckout(): Promise<TwoCoInlineCart> {
  twoCheckout ??= loadScript("https://secure.2checkout.com/checkout/client/twoCoInlineCart.js")
    .then(() => {
      const cart = window.TwoCoInlineCart;
      if (!cart) throw new Error("2Checkout did not load");
      cart.setup.setConfig("app", { merchant: TWO_CHECKOUT_MERCHANT, iframeLoad: "checkout" });
      cart.setup.setConfig("cart", {
        host: "https://secure.2checkout.com",
        customization: "TYPORA-inline-one-step",
      });
      cart.register();
      cart.events.subscribe("cart:closed", () => onTwoCheckoutClosed?.());
      return cart;
    })
    .catch((error) => {
      twoCheckout = null;
      throw error;
    });
  return twoCheckout;
}

// MARK: - FastSpring popup

let fastSpring: Promise<NonNullable<Window["fastspring"]>> | null = null;
let onFastSpringClosed: ((order: { id?: string; reference?: string } | null) => void) | null = null;

/** A completed FastSpring order, from its `order.completed` browser script. */
export type FastSpringOrder = { id: string; email: string; licenses: string[] };

/** Orders the popup forwarded, by id: the payload can come before the popup closes. */
const fastSpringOrders = new Map<string, FastSpringOrder>();
const fastSpringListeners = new Set<(order: FastSpringOrder) => void>();

/** Hears each completed FastSpring order, including ones that already came. */
export function onFastSpringOrder(listener: (order: FastSpringOrder) => void): () => void {
  fastSpringListeners.add(listener);
  fastSpringOrders.forEach(listener);
  return () => fastSpringListeners.delete(listener);
}

const LICENSE_CODE = /^[A-Z0-9]{4,}(?:-[A-Z0-9]{4,})+$/;

/** Every license code in an order's `items[].fulfillments`, whatever its nesting. */
function fulfilledLicenses(value: unknown, found: Set<string>) {
  if (typeof value === "string") {
    for (const part of value.split(/\s+/)) if (LICENSE_CODE.test(part)) found.add(part);
  } else if (Array.isArray(value)) {
    value.forEach((item) => fulfilledLicenses(item, found));
  } else if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    // `{type: "license", license: "…"}`; file links and display text don't count.
    if ("license" in record) fulfilledLicenses(record.license, found);
    else Object.values(record).forEach((item) => fulfilledLicenses(item, found));
  }
}

/**
 * The completed orders in what the popup forwards: one `order.completed`
 * event, a webhook body (`{events: […]}`), or the order itself.
 */
export function parseFastSpringWebhook(data: unknown): FastSpringOrder[] {
  const orders: FastSpringOrder[] = [];
  const visit = (value: unknown) => {
    if (!value || typeof value !== "object") return;
    const record = value as Record<string, any>;
    if (Array.isArray(record.events)) return record.events.forEach(visit);
    if (typeof record.type === "string" && record.data) {
      if (/order\.completed$/.test(record.type)) visit(record.data);
      return;
    }
    const id = record.id ?? record.order;
    const email = record.customer?.email ?? record.email;
    if (typeof id !== "string" || typeof email !== "string" || !Array.isArray(record.items)) return;
    const found = new Set<string>();
    for (const item of record.items) fulfilledLicenses(item?.fulfillments, found);
    if (found.size > 0) orders.push({ id, email, licenses: [...found] });
  };
  visit(data);
  return orders;
}

function loadFastSpring(): Promise<NonNullable<Window["fastspring"]>> {
  window.typoraPurchasePopupClosed = (order) => onFastSpringClosed?.(order);
  window.typoraPurchaseWebhookReceived = (data) => {
    for (const order of parseFastSpringWebhook(data)) {
      fastSpringOrders.set(order.id, order);
      fastSpringListeners.forEach((listener) => listener(order));
    }
  };
  fastSpring ??= loadScript("https://d1f8f9xcsvx3ha.cloudfront.net/sbl/0.8.9/fastspring-builder.min.js", {
    id: "fsc-api",
    "data-storefront": FASTSPRING_STOREFRONT,
    "data-popup-closed": "typoraPurchasePopupClosed",
    "data-popup-webhook-received": "typoraPurchaseWebhookReceived",
  })
    .then(() => {
      if (!window.fastspring) throw new Error("FastSpring did not load");
      return window.fastspring;
    })
    .catch((error) => {
      fastSpring = null;
      throw error;
    });
  return fastSpring;
}

/** Start loading both checkouts early, so Continue opens at once. */
export function preloadCheckouts() {
  loadTwoCheckout().catch(() => {});
  loadFastSpring().catch(() => {});
}

export type CheckoutRequest = {
  provider: Provider;
  country: string;
  quantity: number;
};

export type CheckoutCallbacks = {
  /**
   * FastSpring: payment went through; its licenses come through
   * `onFastSpringOrder`. 2Checkout instead leaves the page for its return
   * URL, which comes back here (`returnedOrderProof`).
   */
  onPaid(fastSpringOrderId: string): void;
  /** The checkout closed without an order. */
  onCancelled(): void;
};

/** Opens the provider's checkout over the page. Rejects if it can't load. */
export async function openCheckout(request: CheckoutRequest, callbacks: CheckoutCallbacks): Promise<void> {
  const { provider, country, quantity } = request;
  if (provider === "2checkout") {
    const cart = await loadTwoCheckout();
    onTwoCheckoutClosed = () => {
      onTwoCheckoutClosed = null;
      callbacks.onCancelled();
    };
    cart.billing.setCountry(country);
    cart.cart.setCurrency(CURRENCIES[country] ?? "USD");
    cart.cart.setLanguage(checkoutLanguage());
    cart.products.removeAll();
    cart.products.add({ code: TWO_CHECKOUT_PRODUCT, quantity });
    // After payment 2Checkout sends the buyer back here with the signed
    // order reference.
    cart.cart.setReturnMethod({ type: "redirect", url: `${window.location.origin}/purchase` });
    cart.cart.checkout();
    return;
  }
  const builder = (await loadFastSpring()).builder;
  onFastSpringClosed = (order) => {
    onFastSpringClosed = null;
    // The popup passes null when it closes before an order completes.
    if (order?.id) callbacks.onPaid(order.id);
    else callbacks.onCancelled();
  };
  builder.country(country);
  builder.language(checkoutLanguage());
  builder.update(FASTSPRING_PRODUCT, quantity);
  builder.checkout();
}

// MARK: - Order status

export type OrderStatus =
  | { status: "pending" }
  | { status: "ready"; email: string; licenses: string[] }
  | { status: "disabled" }
  /** The store couldn't confirm the order from this page; the email still comes. */
  | { status: "unverified" };

export async function fetchOrderStatus(proof: OrderProof, signal?: AbortSignal): Promise<OrderStatus> {
  const response = await fetch(`${STORE_ORIGIN}/api/purchase/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(proof),
    signal,
  });
  if (response.status === 403) return { status: "unverified" };
  if (!response.ok) throw new Error(`status ${response.status}`);
  const body = (await response.json()) as Partial<OrderStatus> & { licenses?: unknown; email?: unknown };
  if (
    body.status === "ready" &&
    typeof body.email === "string" &&
    Array.isArray(body.licenses) &&
    body.licenses.length > 0 &&
    body.licenses.every((license) => typeof license === "string")
  )
    return { status: "ready", email: body.email, licenses: body.licenses as string[] };
  if (body.status === "disabled") return { status: "disabled" };
  return { status: "pending" };
}

/** `typora://license/activate`, handled by Typora 2 (packages/common/src/license/activationLink.ts). */
export function activationLink(email: string, license: string, chinaServer: boolean): string {
  const query = new URLSearchParams({ email, license });
  if (chinaServer) query.set("server", "cn");
  return `typora://license/activate?${query.toString()}`;
}
